/**
 * Permanent redirects the published site serves that the CMS API cannot see.
 *
 * Webflow keeps redirects in site settings rather than in any collection, so no
 * field on a post records one. That hides two different populations.
 *
 * A slug the CMS still reports as live can be redirected away anyway, which is
 * how `/blog/how-to-configure-proxies-on-iphone` arrives with `isDraft: false`
 * and still 301s to `/blog/iphone-proxy` in a browser. Serving it would put
 * near-identical content at two addresses.
 *
 * The rest are slugs the CMS dropped when a post was renamed or folded into the
 * academy. Nothing enumerates those, so the published-slug census cannot reach
 * them by construction: requesting all 224 turns up only the iPhone entry, as
 * it did on 24 August 2026 and again on 31 August 2026. They came instead from
 * live's `/llms.txt`, the Wayback CDX index, and generated rename variants of
 * every live slug, 3606 URLs probed against the published site. Every entry
 * below answered 301 to the target it names.
 *
 * The academy moves are listed one by one rather than folded into a
 * `/blog/puppeteer-*` rule because live's table is not uniform: of the 42
 * academy articles under that stem, 28 are redirected from `/blog` and 14
 * answer 404, so a pattern would serve redirects the published site does not.
 *
 * `/blog/$slug` is the only route serving from this map today, but
 * `prerenderPages` asks it about every prefix. An entry outside `/blog/` would
 * drop its path from the prerendered set and the sitemap while nothing answered
 * the redirect, which loses the page instead of moving it.
 *
 * Re-probe before the domain switch: a redirect added after that date stays
 * invisible here until someone looks.
 */
const PERMANENT_REDIRECTS: Readonly<Record<string, string>> = {
  "/blog/android-proxy": "/blog/android-free-proxy",
  "/blog/how-to-configure-proxies-on-android": "/blog/android-proxy",
  "/blog/how-to-configure-proxies-on-chrome": "/blog/chrome-proxy-settings",
  "/blog/how-to-configure-proxies-on-iphone": "/blog/iphone-proxy",
  "/blog/how-to-set-up-proxies-for-adspower": "/blog/adspower-proxy",
  "/blog/how-to-set-up-proxies-for-bitbrowser": "/blog/bitbrowser-proxy",
  "/blog/how-to-set-up-proxies-for-clonbrowser": "/blog/clonbrowser-proxy",
  "/blog/how-to-set-up-proxies-for-genlogin": "/blog/genlogin-proxy",
  "/blog/how-to-set-up-proxies-for-hidemyacc": "/blog/hidemyacc-proxy",
  "/blog/how-to-set-up-proxies-for-incogniton": "/blog/incogniton-proxy",
  "/blog/how-to-set-up-proxies-for-linken-sphere": "/blog/linken-sphere-proxy",
  "/blog/how-to-set-up-proxies-for-mulogin": "/blog/mulogin-proxy",
  "/blog/how-to-set-up-proxies-for-multilogin": "/blog/multilogin-proxy",
  "/blog/how-to-set-up-proxies-for-undetectable-io":
    "/blog/undetectable-io-proxy",
  "/blog/how-to-use-proxies-with-firefox": "/blog/firefox-proxy",
  "/blog/myspace-proxy": "/blog",
  "/blog/puppeteer-alternatives": "/academy-article/puppeteer-alternatives",
  "/blog/puppeteer-aws-lambda": "/academy-article/puppeteer-aws-lambda",
  "/blog/puppeteer-click": "/academy-article/puppeteer-click",
  "/blog/puppeteer-cluster": "/academy-article/puppeteer-cluster",
  "/blog/puppeteer-cookies": "/academy-article/puppeteer-cookies",
  "/blog/puppeteer-download-images":
    "/academy-article/puppeteer-download-images",
  "/blog/puppeteer-e2e-testing": "/academy-article/puppeteer-e2e-testing",
  "/blog/puppeteer-extra": "/academy-article/puppeteer-extra",
  "/blog/puppeteer-fill-submit-form":
    "/academy-article/puppeteer-fill-submit-form",
  "/blog/puppeteer-get-element": "/academy-article/puppeteer-get-element",
  "/blog/puppeteer-get-html": "/academy-article/puppeteer-get-html",
  "/blog/puppeteer-html-to-pdf": "/academy-article/puppeteer-html-to-pdf",
  "/blog/puppeteer-iframe": "/academy-article/puppeteer-iframe",
  "/blog/puppeteer-input-field": "/academy-article/puppeteer-input-field",
  "/blog/puppeteer-page-reload": "/academy-article/puppeteer-page-reload",
  "/blog/puppeteer-proxy": "/academy-article/puppeteer-proxy",
  "/blog/puppeteer-scraping": "/academy-article/puppeteer-scraping",
  "/blog/puppeteer-screenshot": "/academy-article/puppeteer-screenshot",
  "/blog/puppeteer-scroll": "/academy-article/puppeteer-scroll",
  "/blog/puppeteer-stealth": "/academy-article/puppeteer-stealth",
  "/blog/puppeteer-timeout": "/academy-article/puppeteer-timeout",
  "/blog/puppeteer-user-agent": "/academy-article/puppeteer-user-agent",
  "/blog/puppeteer-vs-cypress": "/academy-article/puppeteer-vs-cypress",
  "/blog/puppeteer-vs-playwright": "/academy-article/puppeteer-vs-playwright",
  "/blog/puppeteer-vs-selenium": "/academy-article/puppeteer-vs-selenium",
  "/blog/puppeteer-wait-for-page-to-load":
    "/academy-article/puppeteer-wait-for-page-to-load",
  "/blog/puppeteer-waitfornavigation":
    "/academy-article/puppeteer-waitfornavigation",
  "/blog/puppeteer-waitforselector":
    "/academy-article/puppeteer-waitforselector",
  "/blog/pyppeteer-proxy": "/academy-article/pyppeteer-proxy",
  "/blog/pyppeteer-tutorial": "/academy-article/pyppeteer-tutorial",
  "/blog/what-are-private-proxies": "/private-proxy",
  "/blog/youtube-proxy": "/youtube-proxy",
};

export function redirectFor(path: string): string | undefined {
  return PERMANENT_REDIRECTS[path];
}
